import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { LoadingButton } from "../../blocks/LoadingButton.js";
import { Typography, Box, Icon } from "@mui/material";
import { getPageRoute } from "src/services";
import TransparentUpdatesExplainer from "src/components/explainers/TransparentUpdatesExplainer.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { useTheme } from "@emotion/react";
import { useDimensions } from "src/utils/useDimensions.js";
import ActionOption from "src/components/blocks/options/ActionOption.js";
import OpenCalendarDialog from "../../blocks/dialogs/OpenCalendarDialog.js";
import { useSubscribe } from "src/utils/useSubscribe.js";
import { customEvent } from "src/utils/gtag.js";

const CalendarExperience = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const { calendar = null } = location.state || {};
  const theme = useTheme();
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const { subscribe } = useSubscribe();
  const [actionSelected, setActionSelected] = useState(null);

  useEffect(() => {
    customEvent({
      name: "landed_on_other_calendar_experience",
      category: "OtherCalendarExperience",
      label: "Landed on Other Calendar Experience",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });
  }, []);

  const actionOptions = [
    {
      id: "sync-automatically",
      title: "Connect your Calendar",
      subtitle: (
        <>
          Know <u>exactly</u> when your calendar last updated. Force refresh if needed.
        </>
      ),
      isPopular: true,
      isDefault: true,
      showAnimation: false, // Removed animation
    },
    {
      id: "status-quo", 
      title: "Status Quo",
      subtitle: (
        <>
          Hope for the best with silent updates and <u>manually verify</u> that you have the latest events.
        </>
      ),
      isPopular: false,
      isDefault: false,
      showAnimation: false,
    },
  ];

  return (
    <InstructionsLayout nav="back">
      <Box sx={{ mb: { xs: 3, sm: 6 }, px: { xs: 0.5, sm: 2 } }}>
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            display: "block",
            mr: "auto",
            color: `${theme.palette.icon.primary}`,
            fontSize: { xs: "1.5rem", sm: "2rem" },
            mb: { xs: 2, sm: 3 },
            fontWeight: 300,
          }}
        >
          visibility_off
        </Icon>
        <Typography
          variant="h1"
          sx={{
            mr: "auto",
            textAlign: "left",
            fontSize: { xs: "2rem", sm: "2.5rem" },
            lineHeight: { xs: "1.2" },
            fontWeight: "600",
            mb: { xs: 2, sm: 3 },
            letterSpacing: "-0.02em",
          }}
        >
          All calendars update silently by default.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mr: "auto",
            textAlign: "left",
            fontSize: { xs: "1rem", sm: "1.125rem" },
            lineHeight: "1.4",
            color: "text.secondary",
            maxWidth: "420px",
          }}
        >
          You won't know when your calendar app last updated or if you have the latest events.
        </Typography>
      </Box>

      {user && !user.hasValidSubscription ? (
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          gap: { xs: 2, sm: 4 },
          px: { xs: 0.5, sm: 2 },
          pb: { xs: 2, sm: 4 }
        }}>
          {actionOptions.map((option) => (
            <ActionOption
              key={option.id}
              {...option}
              isSelected={option.id === actionSelected}
              onSelect={(id) => {
                setActionSelected(id);
                setTimeout(() => {
                  if (id === "sync-automatically") {
                    console.log("CalendarExperience - navigating with calendar:", calendar);
                    navigate(getPageRoute("help", "SUBSCRIBING_CALENDAR_STEPS"), {
                      state: { 
                        calendar, 
                        source: "other_calendar_experience"
                      }
                    });
                  } else if (id === "status-quo") {
                    subscribe({ calendar });
                    setOpenCalendarDialog(true);
                  }
                }, 500);
              }}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ px: { xs: 0.5, sm: 2 }, pb: { xs: 2, sm: 4 } }}>
          <Typography
            variant={"subtitle2"}
            sx={{ 
              textAlign: "left", 
              mr: "auto", 
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: "1rem", sm: "1.125rem" },
              lineHeight: "1.6"
            }}
            color="text.secondary"
          >
            Members save time and are the first to know.
          </Typography>
          <LoadingButton
            size="grown"
            variant="contained"
            color="primary"
            sx={{ 
              mt: { xs: 1, sm: 2 }, 
              height: { xs: "48px", sm: "56px" }, 
              width: "100%",
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: 600
            }}
            onClick={() => navigate(getPageRoute("help", "CALENDAR_CONNECTED"))}
          >
            Get Started
          </LoadingButton>
        </Box>
      )}
      <OpenCalendarDialog
        open={openCalendarDialog}
        handleClose={() => setOpenCalendarDialog(false)}
        calendar={calendar}
      />
    </InstructionsLayout>
  );
};

export default CalendarExperience;
